import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { DangerButton, PrimaryButton, SecondaryButton } from '../../../../components/buttons';
import { EditColumns } from '../../../../components/edit-columns';
import { HintTooltip } from '../../../../components/hint-tooltip';
import { DohFilter } from '../../../../components/product-table/filters/doh-filter';
import { LabelFilter } from '../../../../components/product-table/filters/label-filter';
import { SizeFilter } from '../../../../components/product-table/filters/size-filter';
import { TypeFilter } from '../../../../components/product-table/filters/type-filter';
import { defaultProductSearchPlaceholders, SearchInput } from '../../../../components/search-input';
import { SegmentedToggle } from '../../../../components/segmented-toggle';
import { SortPopover } from '../../../../components/sort-popover';
import { TanStackTableCSVButton } from '../../../../components/tanstack-table/csv-button';
import { PageToolbar } from '../../../../components/toolbar/page-toolbar';
import { ToolbarPopover } from '../../../../components/toolbar/popover';
import { SearchWithCount } from '../../../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../../../contexts/product-table-context';
import { DEFAULT_HIDDEN_COLUMNS, PINNED_COLUMNS } from '../hooks/use-columns';
import type { EditOrderViewModel } from '../hooks/use-edit-order-view';
import { SORT_COLUMN_OPTIONS } from '../hooks/use-sorted-order';
import { ReorderSettingsPopover } from './reorder-settings-popover';
import { ShowFilter } from './show-filter';

type EditOrderToolbarProperties = {
  view: EditOrderViewModel;
  /** Mode-specific controls rendered at the start of the right cluster. */
  rightExtra?: ReactNode;
};

export function EditOrderToolbar({ view, rightExtra }: EditOrderToolbarProperties) {
  return (
    <PageToolbar
      left={
        <>
          <ReorderSettingsPopover
            options={view.options}
            setOptions={view.setOptions}
            storeId={view.store.id}
            vendorId={view.vendor.vendor_id}
          />
          <ProductTableContext.Provider value={view.filterContext}>
            <ToolbarPopover
              icon={<AdjustmentsHorizontalIcon className="size-5" />}
              label="Filters"
              badge={view.activeFilterCount}
              onClear={view.clearFilters}
            >
              <ShowFilter value={view.showFilter} onChange={view.setShowFilter} />
              <SizeFilter products={view.orderProducts} />
              <TypeFilter products={view.orderProducts} />
              <LabelFilter />
              <DohFilter />
            </ToolbarPopover>
          </ProductTableContext.Provider>
          {!view.isVolumeDeals && (
            <SortPopover
              sorting={view.sortPopoverSorting}
              onSortingChange={view.handleSortPopoverChange}
              sortColumnOptions={SORT_COLUMN_OPTIONS}
            />
          )}
          {!view.isVolumeDeals && (
            <EditColumns
              tableKey={view.columnsTableKey}
              pinnedColumns={PINNED_COLUMNS}
              defaultHiddenColumnIds={DEFAULT_HIDDEN_COLUMNS}
              sorting={view.sorting}
            />
          )}
        </>
      }
      right={
        <>
          {rightExtra}
          {view.hasBuyupData && (
            <HintTooltip
              text={view.isSwing ? 'Toggle order/buyup view' : 'Toggle order/deal view'}
              shortcut="meta+b"
            >
              <div>
                <SegmentedToggle
                  value={view.mode}
                  onChange={(value) => view.setBuyupView(value === 'buyup')}
                  options={[
                    { value: 'order', label: 'Order' },
                    { value: 'buyup', label: view.isSwing ? 'Buyup' : 'Deals' },
                  ]}
                />
              </div>
            </HintTooltip>
          )}

          <SearchWithCount
            quantity={view.filteredOrderItems.length}
            total={view.orderItems.length}
            noun="items"
          >
            <SearchInput
              grouped
              value={view.query}
              onChange={(event) => view.handleSearchChange(event.target.value)}
              inputRef={view.searchReference}
              placeholders={defaultProductSearchPlaceholders}
              shortcut="meta+f"
            />
          </SearchWithCount>
          <DangerButton
            onClick={view.promptDeleteOrder}
            disabledReason={view.cancelUnavailableReason}
          >
            Cancel
          </DangerButton>
          <SecondaryButton
            onClick={view.promptResetOrder}
            disabledReason={view.resetUnavailableReason}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton onClick={view.toggleShowSubmitOrder}>Purchase Order</PrimaryButton>
          <TanStackTableCSVButton table={view.table} filename={view.csvFilename} />
        </>
      }
    />
  );
}
